import React, { useState } from "react";
import { useParams } from "react-router-dom";

import { useAppDispatch, useAppSelector } from "../../../app/store";

import SidepeekDetailsAccordion from "../../../components/ui/accordion/v2/SidepeekDetailsAccordion";
import EditableField from "../../../components/ui/formelement/v2/EditableField";

import {
  getStakeholderById,
  getEnrichedStakeholderById,
} from "../logic/stakeholderSlice";

import {
  updateStakeholder,
  updateCustomerDetails,
} from "../logic/stakeholderThunks";

import { showErrorToast } from "../../../lib/toast";

interface GuarantorDetailsProps {
  stakeholderId: string;
}

const GuarantorDetails: React.FC<GuarantorDetailsProps> = ({
  stakeholderId,
}) => {
  const { id: caseId } = useParams<{ id: string }>();

  const dispatch = useAppDispatch();

  const [editingField, setEditingField] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  const stakeholder = useAppSelector((state) =>
    getStakeholderById(state, stakeholderId)
  );

  const enrichedStakeholder = useAppSelector((state) =>
    getEnrichedStakeholderById(state, stakeholderId)
  );

  if (!stakeholder) {
    return null;
  }

  const customerDetails = enrichedStakeholder?.customer_details;

  const handleSave = async (
    field: string,
    value: string | null
  ): Promise<boolean> => {
    if (!caseId) return false;

    setIsSaving(true);
    const trimmedValue = value?.trim() || undefined;

    try {
      if (field === "name") {
        await dispatch(
          updateStakeholder({
            caseId,
            stakeholderId,
            request: { name: trimmedValue || "" },
          })
        ).unwrap();
      } else if (field === "cif_number") {
        await dispatch(
          updateCustomerDetails({
            caseId,
            stakeholderId,
            request: {
              cif_number: trimmedValue,
              constitution: customerDetails?.constitution,
            },
          })
        ).unwrap();
      }

      setEditingField(null);
      return true;
    } catch {
      showErrorToast("Failed to update guarantor details. Please try again.");
      return false;
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <SidepeekDetailsAccordion title="Details">
      <div className="flex w-full flex-col gap-2">
        <EditableField
          label="Name"
          value={stakeholder.name}
          type="text"
          isEditable={true}
          isEditing={editingField === "name"}
          onEditStart={() => setEditingField("name")}
          onEditCancel={() => setEditingField(null)}
          onSave={(value) => handleSave("name", value)}
          disabled={isSaving}
        />

        <EditableField
          label="CIF Number"
          value={customerDetails?.cif_number ?? null}
          type="text"
          isEditable={true}
          isEditing={editingField === "cif_number"}
          onEditStart={() => setEditingField("cif_number")}
          onEditCancel={() => setEditingField(null)}
          onSave={(value) => handleSave("cif_number", value)}
          disabled={isSaving}
        />
      </div>
    </SidepeekDetailsAccordion>
  );
};

export default GuarantorDetails;